import { useState } from "react";
import { Dumbbell, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export function TrainingMakeupPanel() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/training-makeup?dry_run=${dryRun}`);
      setPreview(data);
      if (dryRun) toast.info(`${data.affected} gladiator(s) would be credited for today.`);
      else toast.success(`Credited ${data.applied} gladiator(s) for the missed training day.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="training-makeup-panel" className="mb-6 border border-amber-900/40 bg-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Training Make-up (one-time fix)</h3>
      </div>
      <p className="text-[12px] text-zinc-500 mb-3">
        Credits gladiators that erroneously <span className="text-zinc-300">rested today</span> due to the training bug —
        only those resolved today with a non-empty schedule that earned nothing. Idempotent (safe to re-run; won't double-credit).
        Preview first, then apply.
      </p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="training-makeup-preview" onClick={() => run(true)} disabled={busy}
          className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 rounded-sm transition-colors disabled:opacity-50">
          {busy ? "Working…" : "Preview affected"}
        </button>
        <button data-testid="training-makeup-apply" onClick={() => run(false)} disabled={busy || !preview}
          title={!preview ? "Run a preview first" : "Apply the make-up credit"}
          className="px-4 py-2 text-sm bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide hover:bg-amber-400 rounded-sm transition-colors disabled:opacity-50">
          Apply Make-up
        </button>
      </div>
      {preview && (
        <div data-testid="training-makeup-result" className="mt-3 text-[12px] text-zinc-400 border-t border-zinc-800 pt-3">
          <p><span className="text-zinc-200">{preview.affected}</span> affected · <span className="text-zinc-200">{preview.applied}</span> credited{preview.dry_run ? " (preview only)" : ""} · {preview.date}</p>
          {preview.note && <p className="flex items-center gap-1.5 text-orange-300 mt-1"><AlertTriangle size={12} /> {preview.note}</p>}
          {preview.samples?.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {preview.samples.map((s, i) => (
                <li key={i} className="text-zinc-500">• {s.name}: {Object.entries(s.gains).map(([k, v]) => `+${v} ${k}`).join(", ")}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
